import { useState } from 'react'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'

import CaretDownOutlinedIcon from '@ant-design/icons/CaretDownOutlined'
import CaretRightOutlinedIcon from '@ant-design/icons/CaretRightOutlined'
import DeleteOutlinedIcon from '@ant-design/icons/DeleteOutlined'
import EditOutlinedIcon from '@ant-design/icons/EditOutlined'

import Stack from '@mui/material/Stack'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import useDialog from '@shared/hooks/src/useDialog'
import { RoleGuard } from '@shared/providers/src/MeProvider'
import { handleError, UserRole } from '@shared/utils'

import Confirmation from '@components/Dialog/Confirmation'
import PatientAccessGuard from '@components/PatientAccessGuard'
import StatusChip from '@components/StatusChip'
import TableCollapsableCell from '@components/TableCollapsableCell'

import { useInsurance, useInsuranceRemove, useInsuranceUpdate } from '../Insurances.hooks'
import InsuranceUpdate from '../InsuranceUpdate'
import InsuranceDetailed from './InsuranceDetailed'

export default function InsuranceRow({ patientId, data, columns, extended = false }) {
  const [detailedInfoOpen, setDetailedInfoOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  const detailedInsurance = useInsurance(patientId, data.id, { enabled: editOpen || detailedInfoOpen })

  const update = useInsuranceUpdate(patientId, data.id)
  const remove = useInsuranceRemove(patientId)

  const confirmRemove = useDialog({
    component: Confirmation,
    props: ({ item: insuranceId, close }) => ({
      title: 'Delete Insurance?',
      description: 'Are you sure you want to delete this insurance record?',
      rejectLabel: 'Cancel',
      confirmLabel: 'Delete',
      onReject: () => close(),
      onConfirm: () =>
        remove
          .mutateAsync(insuranceId)
          .then(() => {
            close()
            toast.success('Insurance has been deleted')
          })
          .catch(handleError),
    }),
  })

  return (
    <>
      <TableRow hover onClick={() => setDetailedInfoOpen((o) => !o)} sx={{ cursor: 'pointer' }}>
        <TableCell padding="checkbox">
          <IconButton size="small">{detailedInfoOpen ? <CaretDownOutlinedIcon /> : <CaretRightOutlinedIcon />}</IconButton>
        </TableCell>
        {extended && <TableCell sx={{ verticalAlign: 'top' }}>{data.id}</TableCell>}
        <TableCell sx={{ verticalAlign: 'top' }}>
          <Typography sx={{ fontWeight: 600 }} noWrap>
            {data.company}
          </Typography>
          {data.subscriber && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
              {data.subscriber}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          {extended ? (
            <>
              <Stack direction="row">
                <Typography sx={{ width: 56, color: 'text.secondary' }}>ID</Typography>
                <Typography>{data.idnum}</Typography>
              </Stack>
              <Stack direction="row">
                <Typography sx={{ width: 56, color: 'text.secondary' }}>Group</Typography>
                <Typography>{data.groupnum}</Typography>
              </Stack>
            </>
          ) : (
            <>
              <Typography variant="body2">{data.idnum || '-'}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {data.groupnum || '-'}
              </Typography>
            </>
          )}
        </TableCell>
        {extended && (
          <TableCell sx={{ verticalAlign: 'top' }}>
            <Stack direction="row">
              <Typography sx={{ width: 48, color: 'text.secondary' }}>BIN</Typography>
              <Typography>{data.bin}</Typography>
            </Stack>
            <Stack direction="row">
              <Typography sx={{ width: 48, color: 'text.secondary' }}>PCN</Typography>
              <Typography>{data.pcn}</Typography>
            </Stack>
          </TableCell>
        )}
        <TableCell sx={{ verticalAlign: 'top' }}>{data.created ? dayjs(data.created).format('L') : '-'}</TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>{data.validatedByAvita && <StatusChip label="Validated" type="success" icon />}</TableCell>
        <RoleGuard allowed={[UserRole.Admin, UserRole.MA, UserRole.Support, UserRole.Provider]}>
          <PatientAccessGuard patientId={patientId}>
            <TableCell align="right" sx={{ verticalAlign: 'top' }}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'flex-end' }}>
                <IconButton
                  variant="outlined"
                  size="small"
                  shape="rounded"
                  disabled={remove.isPending || update.isPending}
                  onClick={(e) => {
                    e.stopPropagation()
                    confirmRemove(data.id)
                  }}
                >
                  <DeleteOutlinedIcon />
                </IconButton>
                <IconButton
                  variant="contained"
                  color="inherit"
                  size="small"
                  shape="rounded"
                  disabled={remove.isPending || update.isPending || (editOpen && detailedInsurance.isPending)}
                  onClick={(e) => {
                    e.stopPropagation()
                    setEditOpen(true)
                  }}
                >
                  <EditOutlinedIcon />
                </IconButton>
              </Stack>
            </TableCell>
          </PatientAccessGuard>
        </RoleGuard>
      </TableRow>
      <TableRow>
        <TableCollapsableCell open={detailedInfoOpen} colSpan={columns}>
          <InsuranceDetailed data={detailedInsurance.data} isLoading={detailedInsurance.isPending} />
        </TableCollapsableCell>
      </TableRow>
      <InsuranceUpdate
        open={Boolean(editOpen && detailedInsurance.data)}
        insurance={detailedInsurance.data}
        onUpdate={update.mutateAsync}
        onClose={() => setEditOpen(false)}
      />
    </>
  )
}
